/**
 * Notification Factory Utility
 * Creates notification objects with proper defaults and unique IDs
 */

import { 
  NotificationObject, 
  NotificationKind, 
  NotificationType,
  AnimationState 
} from '../types';

/**
 * Generate a unique ID for notifications
 */
export const generateNotificationId = (): string => {
  return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Maximum length for notification subtitles
 * With 3-line limit and 0.875rem font: ~45 chars per line = 135 chars total
 * This allows useful error context while preventing overly long messages
 */
const MAX_SUBTITLE_LENGTH = 135;

/**
 * Truncate text to maximum length with ellipsis
 */
export const truncateSubtitle = (text: string, maxLength: number = MAX_SUBTITLE_LENGTH): string => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength - 1) + '…';
};

/**
 * Default notification configuration
 */
const DEFAULT_NOTIFICATION: Partial<NotificationObject> = {
  type: 'toast',
  kind: 'info',
  duration: 4000,
  priority: 0,
  lowContrast: false,
  hideCloseButton: false,
  preventDuplicate: false,
  animationState: 'entering',
  role: 'status',
};

/**
 * Default durations by notification kind
 */
const DEFAULT_DURATIONS: Record<NotificationKind, number | undefined> = {
  success: 4000,
  info: 5000,
  warning: 6000,
  error: undefined, // Errors persist by default
};

/**
 * Default roles by notification type
 */
const DEFAULT_ROLES: Record<NotificationType, 'alert' | 'log' | 'status' | 'alertdialog'> = {
  toast: 'status',
  inline: 'status',
  actionable: 'alertdialog',
  callout: 'status',
};

/**
 * Create a notification object with defaults
 */
export const createNotification = (
  partial: Partial<NotificationObject>
): NotificationObject => {
  const id = partial.id || generateNotificationId();
  const timestamp = Date.now();
  const type = partial.type || DEFAULT_NOTIFICATION.type!;
  const kind = partial.kind || DEFAULT_NOTIFICATION.kind!;
  
  // Set duration based on kind if not explicitly provided
  const duration = partial.duration !== undefined 
    ? partial.duration 
    : DEFAULT_DURATIONS[kind];

  // Set role based on type if not explicitly provided
  const role = partial.role || DEFAULT_ROLES[type];
  
  // Auto-truncate subtitle if provided and too long
  const subtitle = partial.subtitle ? truncateSubtitle(partial.subtitle) : undefined;

  return {
    ...DEFAULT_NOTIFICATION,
    ...partial,
    id,
    timestamp,
    type,
    kind,
    duration,
    role,
    subtitle,
    animationState: 'entering' as AnimationState,
  } as NotificationObject;
};

/**
 * Create a success notification
 */
export const createSuccessNotification = (
  title: string,
  options?: Partial<NotificationObject>
): NotificationObject => {
  return createNotification({
    ...options,
    title,
    kind: 'success',
  });
};

/**
 * Create an error notification
 */
export const createErrorNotification = (
  title: string,
  options?: Partial<NotificationObject>
): NotificationObject => {
  return createNotification({
    ...options,
    title,
    kind: 'error',
  });
};

/**
 * Create a warning notification
 */
export const createWarningNotification = (
  title: string,
  options?: Partial<NotificationObject>
): NotificationObject => {
  return createNotification({
    ...options,
    title,
    kind: 'warning',
  });
};

/**
 * Create an info notification
 */
export const createInfoNotification = (
  title: string,
  options?: Partial<NotificationObject>
): NotificationObject => {
  return createNotification({
    ...options,
    title,
    kind: 'info',
  });
};

/**
 * Create an actionable notification
 */
export const createActionableNotification = (
  title: string,
  actionLabel: string,
  onActionClick: () => void,
  options?: Partial<NotificationObject>
): NotificationObject => {
  return createNotification({
    ...options,
    title,
    actionLabel,
    onActionClick,
    type: 'actionable',
    role: 'alertdialog',
  });
};

/**
 * Create a notification key for deduplication
 */
export const getNotificationKey = (notification: NotificationObject): string => {
  // Create a key based on content for deduplication
  const contentKey = [
    notification.type,
    notification.kind,
    notification.title,
    notification.subtitle,
    notification.groupId,
  ].filter(Boolean).join('-');
  
  return contentKey;
};

/**
 * Check if two notifications are duplicates
 */
export const areNotificationsDuplicate = (
  a: NotificationObject,
  b: NotificationObject
): boolean => {
  if (!a.preventDuplicate && !b.preventDuplicate) {
    return false;
  }
  
  return getNotificationKey(a) === getNotificationKey(b);
};

/**
 * Sort notifications by priority (higher priority first)
 * If priority is the same, newer notifications come first
 */
export const sortNotificationsByPriority = (
  a: NotificationObject,
  b: NotificationObject
): number => {
  // First sort by priority (higher first)
  if ((a.priority || 0) !== (b.priority || 0)) {
    return (b.priority || 0) - (a.priority || 0);
  }
  
  // Then by timestamp (newer first)
  return b.timestamp - a.timestamp;
};
